import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { GenericTable } from '@app/components/GenericTable/GenericTable';
import { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ListResponse } from '@app/models';
import { AuditLogInterface, AuditLogAction } from '@app/models/log/audit';
import { DateRangePicker } from '@app/components/DateRangePicker/DateRangePicker';
import { UsersSelect } from '@app/components/UsersSelect/UsersSelect';
import { BusinessesSelect } from '@app/components/BusinessesSelect/BusinessesSelect';
import SearchIcon from '@app/assets/icons/search.svg?react';
import CalendarIcon from '@app/assets/icons/calendar-01.svg?react';
import { OrderDir, OrderDirType } from '@app/constants/core';
import {
  AUDIT_LOG_ACTION_OPTIONS,
  getAuditLogActionColor,
} from '@app/constants/audit-log';
import { Select } from '@app/components/Select/Select';
import { useNavigate } from 'react-router-dom';
import { getAuditLogs } from '@app/services/admin';

const HEADER_COLUMNS = [
  { label: 'Date', field: 'createdAt', sortable: true },
  { label: 'Action', field: 'action', sortable: false },
  { label: 'Admin', field: 'admin', sortable: false },
  { label: 'User', field: 'user', sortable: false },
  { label: 'Business', field: 'business', sortable: false },
];

interface AuditLogsResponse extends ListResponse<AuditLogInterface> {}

interface FormType {
  orderDir: OrderDirType;
  page: number;
  perPage: number;
  userId?: string;
  businessId?: string;
  startDate?: string;
  endDate?: string;
  action?: AuditLogAction;
}

export const AdminAuditLogs = () => {
  const navigate = useNavigate();
  const form = useForm<FormType>({
    defaultValues: {
      orderDir: OrderDir.desc,
      page: 1,
      perPage: 10,
      userId: undefined,
      businessId: undefined,
      startDate: '',
      endDate: '',
      action: undefined,
    },
  });

  const {
    action,
    orderDir,
    page,
    perPage,
    userId,
    businessId,
    startDate,
    endDate,
  } = form.watch();

  const { data, isLoading, isError } = useQuery<AuditLogsResponse>({
    queryKey: [
      'logs',
      'audit',
      {
        page,
        perPage,
        userId,
        businessId,
        orderDir,
        startDate,
        endDate,
        action,
      },
    ],
    queryFn: () =>
      getAuditLogs({
        page,
        perPage,
        orderDir,
        ...(userId && { userId }),
        ...(businessId && { businessId }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(action && { action }),
      }),
    placeholderData: keepPreviousData,
  });

  const totalPages = useMemo(() => {
    if (!data) return 0;
    const responseData = data as AuditLogsResponse;
    return Math.ceil(responseData.total / responseData.perPage);
  }, [data]);

  const handlePrevPage = () => {
    if (page > 1) {
      form.setValue('page', page - 1);
    }
  };

  const handleNextPage = () => {
    if (data && page < totalPages) {
      form.setValue('page', page + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Audit Logs</h2>
          <div className="w-64 h-9 bg-gray-200 rounded-md animate-pulse"></div>
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-16 bg-gray-100 rounded-md animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Audit Logs</h2>
        <div className="p-4 bg-red-50 text-red-700 rounded-md">
          Error loading audit logs. Please try again later.
        </div>
      </div>
    );
  }

  const handleLimitChange = (limit?: number) => {
    if (limit) {
      form.setValue('perPage', limit);
      form.setValue('page', 1);
    }
  };

  const handleSort = () => {
    form.setValue('orderDir', orderDir === 'desc' ? 'asc' : 'desc');
  };

  const handleUserSelect = (selectedUserId?: string) => {
    form.setValue('userId', selectedUserId || undefined);
    form.setValue('page', 1);
  };

  const handleBusinessSelect = (selectedBusinessId?: string) => {
    form.setValue('businessId', selectedBusinessId || undefined);
    form.setValue('page', 1);
  };

  const handleActionSelect = (selectedAction?: AuditLogAction) => {
    form.setValue('action', selectedAction || undefined);
    form.setValue('page', 1);
  };

  const renderAuditLogRow = (logs: AuditLogInterface[]): ReactNode => {
    return logs.map((log) => (
      <tr
        key={log.id}
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => navigate(`/admin/logs/audit/${log.id}`)}
      >
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {format(new Date(log.createdAt), 'PPpp')}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getAuditLogActionColor(log.action)}`}
          >
            {log.action.replace('_', ' ')}
          </span>
        </td>
        <td className="px-6 py-4">
          <div className="text-sm font-medium text-gray-900">
            {log.admin?.firstName} {log.admin?.lastName}
          </div>
          <div className="text-sm text-gray-500">{log.admin?.email}</div>
        </td>
        <td className="px-6 py-4">
          {log.user ? (
            <div>
              <div className="text-sm font-medium text-gray-900">
                {log.user.firstName} {log.user.lastName}
              </div>
              <div className="text-sm text-gray-500">{log.user.email}</div>
            </div>
          ) : (
            <span className="text-sm text-gray-500">-</span>
          )}
        </td>
        <td className="px-6 py-4">
          {log.business ? (
            <div>
              <div className="text-sm font-medium text-gray-900">
                {log.business.name}
              </div>
              <div className="text-sm text-gray-500">{log.business.email}</div>
            </div>
          ) : (
            <span className="text-sm text-gray-500">-</span>
          )}
        </td>
      </tr>
    ));
  };

  const handleFiltersReset = () => {
    form.reset();
    form.setValue('page', 1);
  };

  const hasActiveFilters = Boolean(
    userId || businessId || startDate || endDate || action
  );
  const hasData = Boolean(data?.data && data.data.length > 0);

  return (
    <FormProvider {...form}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Audit Logs</h2>
        </div>
        <div className="bg-white w-full p-4 space-y-4">
          <div className="flex flex-row gap-4 flex-wrap">
            <DateRangePicker
              startName="startDate"
              endName="endDate"
              startPlaceholder="Start date"
              endPlaceholder="End date"
              className="bg-gray-100 border-0 rounded-lg px-4 py-2 text-gray-600 placeholder:text-gray-500 focus:bg-gray-200 focus:outline-none !ml-0"
              containerClass="flex-shrink-0"
              position="absolute"
            />

            <UsersSelect
              onChange={handleUserSelect}
              value={userId}
              name="userId"
              placeholder="All Users"
            />
            <BusinessesSelect
              onChange={handleBusinessSelect}
              value={businessId}
              name="businessId"
            />
            <Select
              options={AUDIT_LOG_ACTION_OPTIONS}
              onChange={(value) => handleActionSelect(value as AuditLogAction)}
              value={action || ''}
              name="action"
              placeholder="All Actions"
            />
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleFiltersReset}
                className="flex items-center gap-1 text-red-500 hover:text-red-600 transition-colors cursor-pointer"
              >
                <span className="text-sm">Reset</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
        {!hasData && !isLoading && hasActiveFilters && (
          <>
            <br />
            <br />
            <br />
            <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-20">
              <div className="rounded-lg bg-white p-4">
                <SearchIcon />
              </div>
              <p className="font-bold">No audit logs</p>
              <p className="text-sm text-base-coolGray text-center">
                No audit logs match your current filters.
                <br /> Consider adjusting them.
              </p>
            </div>
          </>
        )}
        {!hasData && !isLoading && !hasActiveFilters && (
          <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-20">
            <div className="rounded-lg bg-white p-4">
              <CalendarIcon />
            </div>
            <p className="font-bold">No audit logs</p>
            <p className="text-sm text-base-coolGray text-center">
              There are no audit logs to display.
              <br />
              Audit logs will appear here when administrative actions are
              performed.
            </p>
          </div>
        )}
        {data?.data && data.data!.length > 0 && !isLoading && (
          <div className="overflow-auto bg-white">
            <GenericTable<AuditLogInterface>
              data={data?.data || []}
              headers={HEADER_COLUMNS}
              renderRow={renderAuditLogRow}
              currentPage={page}
              isLoading={isLoading}
              currentSortBy="createdAt"
              currentSortOrder={orderDir as 'asc' | 'desc'}
              onSort={handleSort}
              pagination={{
                total: data?.total || 0,
                handleNextPage,
                handlePrevPage,
                handleLimitChange,
              }}
            />
          </div>
        )}
      </div>
    </FormProvider>
  );
};

export default AdminAuditLogs;
